import { HttpErrorResponse } from "@angular/common/http";

export interface PhmsIntakeSubmissionFormValues {
    incarceratedPersonDisplayName: string;
    incarceratedPersonIdentifier: string;
    programScopeKey: string;
    /** Full-record fields (story 1438 create-inmate path): DATE inputs yield string or Date. */
    dateOfBirth: string | Date | null;
    facilityCode: string;
    offenseCategoryCode: string;
    /** Controlling-offense description; becomes the case-context offense record. */
    offenseSummary: string;
    eligibilityDate: string | Date | null;
    hearingDate: string | Date | null;
}

export interface PhmsIntakeSubmissionCreateRequest {
    incarceratedPersonDisplayName: string;
    incarceratedPersonIdentifier: string;
    programScopeKey: string;
}

export interface PhmsIntakeSubmissionDto {
    id: number;
    incarceratedPersonDisplayName: string;
    incarceratedPersonIdentifier: string;
    programScopeKey: string;
    intakeStatusCode: string;
    intakeAtUtc: string;
}

export interface PhmsIntakeSubmissionListQuery {
    pageNumber?: number;
    pageSize?: number;
    statusCode?: string;
}

export interface PhmsIntakeSubmissionListResult {
    items: PhmsIntakeSubmissionDto[];
    pageNumber: number;
    pageSize: number;
    totalCount: number;
}

export interface PhmsIntakeProblemDetails {
    type?: string;
    title?: string;
    detail?: string;
    status?: number;
    problemCode?: string;
    issueCodes?: string[];
    errors?: Record<string, string[] | string>;
}

export interface PhmsIntakeDisplayError {
    title: string;
    messages: string[];
    statusCode?: number;
    problemCode?: string;
    issueCodes: string[];
    correlationId?: string | null;
}

export function mapPhmsIntakeError(error: unknown): PhmsIntakeDisplayError {
    if (error instanceof HttpErrorResponse) {
        const problem = toProblemDetails(error.error);
        const messages = new Set<string>();

        flattenValidationMessages(problem.errors).forEach((message) => messages.add(message));

        if (typeof problem.detail === "string" && problem.detail.trim().length > 0) {
            messages.add(problem.detail.trim());
        }

        if (messages.size === 0) {
            messages.add(getDefaultErrorMessage(error.status));
        }

        return {
            title: normalizeText(problem.title) || getDefaultErrorTitle(error.status),
            messages: [...messages],
            statusCode: error.status || problem.status,
            problemCode: normalizeText(problem.problemCode) || undefined,
            issueCodes: Array.isArray(problem.issueCodes)
                ? problem.issueCodes.filter((code): code is string => normalizeText(code).length > 0)
                : [],
            correlationId: error.headers?.get("X-Correlation-ID"),
        };
    }

    return {
        title: "Unexpected error",
        messages: ["The intake submission could not be completed. Try again or contact support if the problem persists."],
        issueCodes: [],
    };
}

function toProblemDetails(errorBody: unknown): PhmsIntakeProblemDetails {
    if (!errorBody || typeof errorBody !== "object" || Array.isArray(errorBody)) {
        return {};
    }

    return errorBody as PhmsIntakeProblemDetails;
}

function flattenValidationMessages(errors: PhmsIntakeProblemDetails["errors"]): string[] {
    if (!errors || typeof errors !== "object") {
        return [];
    }

    return Object.values(errors).flatMap((entry) => {
        if (Array.isArray(entry)) {
            return entry.map((item) => normalizeText(item)).filter((item) => item.length > 0);
        }

        const normalized = normalizeText(entry);
        return normalized.length > 0 ? [normalized] : [];
    });
}

function getDefaultErrorTitle(statusCode: number): string {
    switch (statusCode) {
        case 400:
            return "Request could not be completed";
        case 401:
            return "Sign-in required";
        case 403:
            return "Access denied";
        case 404:
            return "Intake endpoint not found";
        case 409:
            return "Conflict detected";
        default:
            return "Unexpected error";
    }
}

function getDefaultErrorMessage(statusCode: number): string {
    switch (statusCode) {
        case 400:
            return "Review the highlighted values and try again.";
        case 401:
            return "Your session is no longer authenticated. Sign in again and retry the action.";
        case 403:
            return "You do not have permission to submit intake submissions.";
        case 404:
            return "The intake submission endpoint could not be reached.";
        case 409:
            return "The intake submission conflicts with the current state.";
        default:
            return "The intake submission could not be completed. Try again or contact support if the problem persists.";
    }
}

function normalizeText(value: unknown): string {
    return typeof value === "string" ? value.trim() : "";
}

